import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { TeamStep } from './consent/AlertsPopup';
import axios from 'axios';
import { Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LIVE = ['1H', '2H', 'HT', 'ET', 'P', 'BT', 'LIVE'];

const dayLabel = (m) => {
  const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const d = new Date(`${m.match_date}T12:00:00`);
  const diff = Math.round((d - new Date(`${today}T12:00:00`)) / 86400000);
  return diff === 0 ? 'Oggi' : diff === 1 ? 'Domani' : d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' });
};

const LONG_PRESS_MS = 550;
const HINT_KEY = 'fav_team_longpress_hint_seen';
const hintSeen = () => { try { return !!localStorage.getItem(HINT_KEY); } catch { return true; } };

const ChangeTeamSheet = ({ onPick, onClose }) => (
  <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="change-team-overlay">
    <div className="w-full sm:max-w-md bg-gradient-to-b from-[#1F1F1F] to-[#171717] rounded-t-3xl sm:rounded-3xl border-t sm:border border-[#F5A623]/30 p-6" onClick={(e) => e.stopPropagation()} data-testid="change-team-sheet">
      <TeamStep onPick={onPick} onSkip={onClose} skipLabel="Annulla" title="Cambia squadra del cuore" />
    </div>
  </div>
);

const Logo = ({ src, testId }) => (
  <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center" data-testid={testId}>
    {src && <img src={src} alt="" draggable={false} className="w-5 h-5 object-contain pointer-events-none" />}
  </div>
);

// Square card (Home header, right of "Benvenuto") with the next or live match of the user's favourite team.
export const FavoriteTeamCard = ({ liveById, onOpen }) => {
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(false);
  const [showHint, setShowHint] = useState(() => !hintSeen());
  const timer = useRef(null);
  const longPressed = useRef(false);
  const load = useCallback(() => axios.get(`${API}/user/favorite-team/next`).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  const pressStart = () => {
    longPressed.current = false;
    timer.current = setTimeout(() => {
      longPressed.current = true;
      if (navigator.vibrate) navigator.vibrate(15);
      setPicking(true);
      setShowHint(false);
    }, LONG_PRESS_MS);
  };
  const pressEnd = () => clearTimeout(timer.current);
  const pick = async (t) => {
    setPicking(false);
    try {
      await axios.post(`${API}/auth/alerts-popup`, { action: 'team', team: t });
      toast.success(`Squadra del cuore: ${t.name}`);
      load();
    } catch {
      toast.error('Non sono riuscito a salvare la squadra, riprova');
    }
  };
  const m = data?.match;
  // Shown once: the first time the card appears it is marked as seen.
  useEffect(() => {
    if (m && showHint) { try { localStorage.setItem(HINT_KEY, '1'); } catch { /* ignore */ } }
  }, [m, showHint]);
  if (!m) return null;
  const live = liveById?.[m.fixture_id];
  const isLive = live ? LIVE.includes(live.status_short) : m.is_live;
  const sh = live?.home_score ?? m.score_home ?? 0;
  const sa = live?.away_score ?? m.score_away ?? 0;
  const elapsed = live?.elapsed ?? m.elapsed;
  return (
    <>
    {picking && <ChangeTeamSheet onPick={pick} onClose={() => setPicking(false)} />}
    <div className="relative flex-shrink-0">
    <button
      onClick={() => { if (!longPressed.current) onOpen(m.fixture_id); }}
      onPointerDown={pressStart}
      onPointerUp={pressEnd}
      onPointerLeave={pressEnd}
      onPointerCancel={pressEnd}
      onContextMenu={(e) => e.preventDefault()}
      title="Tieni premuto per cambiare squadra"
      style={{ WebkitTouchCallout: 'none' }}
      className="select-none flex-shrink-0 w-[120px] h-[80px] rounded-2xl border border-[#F5A623]/25 bg-[#202020] px-2 py-1.5 flex flex-col items-center justify-between transition-transform active:scale-95"
      data-testid="favorite-team-card"
      aria-label={`${m.home_team} - ${m.away_team}`}
    >
      <div className="flex items-center gap-1 text-[8.5px] font-bold uppercase tracking-wide whitespace-nowrap text-gray-400" data-testid="favorite-team-card-label">
        <Heart className="w-2.5 h-2.5 text-[#FF6B6B] fill-[#FF6B6B]" /> La tua squadra
      </div>
      <div className="flex items-center gap-1.5">
        <Logo src={m.home_logo} testId="favorite-team-card-home" />
        {isLive ? (
          <span className="text-[15px] font-black text-[#F5A623] tabular-nums" data-testid="favorite-team-card-score">{sh}-{sa}</span>
        ) : (
          <span className="text-[10px] font-bold text-gray-500">vs</span>
        )}
        <Logo src={m.away_logo} testId="favorite-team-card-away" />
      </div>
      {isLive ? (
        <span className="px-1.5 py-0.5 rounded-md bg-red-500 text-white text-[9px] font-black" data-testid="favorite-team-card-live">
          LIVE {elapsed ? `${elapsed}'` : ''}
        </span>
      ) : (
        <span className="text-[10px] font-bold text-[#F5A623] capitalize" data-testid="favorite-team-card-when">
          {dayLabel(m)} · {m.match_time}
        </span>
      )}
    </button>
    {showHint && (
      <div className="absolute right-0 top-full mt-1 whitespace-nowrap text-[9px] font-semibold text-gray-500 animate-pulse" data-testid="favorite-team-longpress-hint">
        Tieni premuto per cambiare squadra
      </div>
    )}
    </div>
    </>
  );
};

export default FavoriteTeamCard;
